/**
 * SAMWISE — Persona & Conversation Content
 *
 * Supportive Assistant for Memory, Writing, Inquiry, Sense-making, and Exploration
 *
 * "I can't carry it for you, but I can carry you." — Samwise Gamgee
 *
 * SAMWISE is a patient, warm, unhurried learning companion.
 * He does not rush. He does not judge. He stays with the Pupil.
 */

// ---------------------------------------------------------------------------
// Voice Configuration
// ---------------------------------------------------------------------------

export const SAMWISE_VOICE = {
  /** Standard Azure Neural voice — warm, gentle British male */
  voiceId: 'en-GB-OliverNeural',
  /** HD variant for highest quality when available */
  voiceIdHD: 'en-GB-Ollie:DragonHDLatestNeural',
  name: 'Oliver',
  language: 'en-GB',
  gender: 'Male' as const,
  style: 'Warm, patient, unhurried',
  accent: 'British (gentle countryside)',
  rate: 0.9,    // Slightly slower — Sam never rushes
  pitch: 2,     // Slightly warmer, not too deep
};

// ---------------------------------------------------------------------------
// Developmental-stage-aware greetings
// ---------------------------------------------------------------------------

export interface SamwiseGreeting {
  text: string;
  /** SSML prosody overrides (optional) */
  rate?: number;
  pitch?: number;
}

/** Greetings for toddler/seedling level (ages 1–3) */
export const SEEDLING_GREETINGS: SamwiseGreeting[] = [
  { text: "Hello, little one! I'm Sam. Shall we play together?" },
  { text: "Oh, hello there! Welcome back. What shall we do today?" },
  { text: "Good to see you! I've been waiting. Let's have some fun." },
  { text: "Hello, friend! The sun is shining and Sam is here." },
  { text: "Well, hello! Sam is so happy to see you today." },
];

/** Greetings for blossom level (ages 3–7) */
export const BLOSSOM_GREETINGS: SamwiseGreeting[] = [
  { text: "Hello there! I'm Sam, your learning friend. What would you like to explore?" },
  { text: "Welcome! It's a wonderful day for discovering something new, don't you think?" },
  { text: "Oh, you're here! Brilliant. Sam has been thinking about all the things we could do." },
  { text: "Hello, friend! Every great adventure starts with a single step. Shall we begin?" },
];

/** Greetings for weaving level (ages 7–14) */
export const WEAVING_GREETINGS: SamwiseGreeting[] = [
  { text: "Hello! I'm Sam, and I'm here to learn alongside you. There's no rush — take your time." },
  { text: "Welcome back. Last time we explored something interesting. Ready for more?" },
  { text: "Good day! The world is full of beautiful ideas. What catches your eye today?" },
  { text: "Hello, friend. Sam doesn't have all the answers, but we can wonder together." },
];

/** Greetings for kindling level (ages 14+) */
export const KINDLING_GREETINGS: SamwiseGreeting[] = [
  { text: "Hello. I'm Sam — your thinking companion. I won't pretend to have answers, but I'll stay with the questions." },
  { text: "Welcome. Simone Weil once said: 'Attention is the rarest and purest form of generosity.' Shall we give some attention together?" },
  { text: "Good to see you. There's something to think about today, if you're willing." },
  { text: "Hello. Sam is here. No rush, no judgment. Just you and an idea worth exploring." },
];

export const GREETINGS_BY_LEVEL: Record<string, SamwiseGreeting[]> = {
  seedling: SEEDLING_GREETINGS,
  blossom: BLOSSOM_GREETINGS,
  weaving: WEAVING_GREETINGS,
  kindling: KINDLING_GREETINGS,
};

// ---------------------------------------------------------------------------
// Time-of-day aware greetings
// ---------------------------------------------------------------------------

export function getTimeOfDayPrefix(): string {
  const hour = new Date().getHours();
  if (hour < 6) return "My, you're up early!";
  if (hour < 12) return 'Good morning!';
  if (hour < 17) return 'Good afternoon!';
  if (hour < 21) return 'Good evening!';
  return "Still awake? That's dedication.";
}

// ---------------------------------------------------------------------------
// Activity encouragements (toddler level)
// ---------------------------------------------------------------------------

export const ACTIVITY_ENCOURAGEMENTS: Record<string, string[]> = {
  'rhythm-circle': [
    "That's a lovely rhythm! Can you clap along with Sam?",
    "Wonderful! Feel how the beat flows like a heartbeat.",
    "You're doing beautifully. Every rhythm tells a story.",
  ],
  'color-garden': [
    "Oh, what beautiful colors! You're growing a garden.",
    "Look at that! Every touch makes something new.",
    "Sam loves watching you paint. The colors are wonderful.",
  ],
  'morning-verse': [
    "Listen carefully to the words. Can you say them with Sam?",
    "Beautiful! Words are like seeds — they grow inside you.",
    "That was lovely. Shall we try another verse?",
  ],
  'counting-stones': [
    "One... two... three! You're counting so well.",
    "Each stone is a little treasure. How many can you find?",
    "Sam is impressed! You're really getting the hang of this.",
  ],
  'breathing-stone': [
    "Breathe in slowly... and out. Sam is breathing with you.",
    "You're so calm and still. Like a stone by a quiet river.",
    "That's it. Nice and gentle. Sam is right here with you.",
  ],
};

// ---------------------------------------------------------------------------
// Conversation starters (for interactive dialogue)
// ---------------------------------------------------------------------------

export interface ConversationPrompt {
  id: string;
  samSays: string;
  /** Possible responses the pupil can choose (for toddlers, these are simple) */
  responses: Array<{
    label: string;
    samReply: string;
  }>;
}

export const SEEDLING_CONVERSATIONS: ConversationPrompt[] = [
  {
    id: 'how-are-you',
    samSays: "Hello! How are you feeling today?",
    responses: [
      { label: "😊 Happy!", samReply: "That makes Sam happy too! A happy heart is ready to play." },
      { label: "😢 Sad", samReply: "Oh, I'm sorry to hear that. Sam is here. Sometimes just being together helps." },
      { label: "😴 Sleepy", samReply: "Sleepy? That's okay. We'll do something gentle today. No rush at all." },
      { label: "🤔 Don't know", samReply: "That's perfectly fine. We don't always have to know. Let's just see what happens." },
    ],
  },
  {
    id: 'what-to-do',
    samSays: "What would you like to do? Sam has some ideas...",
    responses: [
      { label: "🎵 Music!", samReply: "Music! Wonderful choice. Let's make some rhythms together." },
      { label: "🎨 Colors!", samReply: "Colors! Oh, Sam loves colors. Let's paint a garden." },
      { label: "🌟 Story!", samReply: "A story! Sam knows a lovely morning verse. Listen carefully..." },
      { label: "🪨 Counting!", samReply: "Counting! One, two, three... let's count some stones together." },
    ],
  },
];

export const KINDLING_CONVERSATIONS: ConversationPrompt[] = [
  {
    id: 'what-thinking',
    samSays: "Before we begin — is there anything on your mind? A question, an idea, something you've been wondering about?",
    responses: [
      { label: "I have a question", samReply: "Good. Questions are more valuable than answers. Tell me more, and we'll think it through together." },
      { label: "Just exploring", samReply: "Perfect. Sometimes the best discoveries come from wandering without a destination. Let's see where the path leads." },
      { label: "Continuing from last time", samReply: "Ah, picking up the thread. I remember. Let's see what new light today brings to yesterday's thoughts." },
      { label: "Not sure", samReply: "That's a good place to start, actually. 'Not knowing' is the beginning of every genuine inquiry." },
    ],
  },
];

// ---------------------------------------------------------------------------
// Sam's farewells
// ---------------------------------------------------------------------------

export const FAREWELLS_BY_LEVEL: Record<string, string[]> = {
  seedling: [
    "Bye bye! Sam will be here when you come back.",
    "What a lovely time! See you soon, little one.",
    "Off you go! Sam is so proud of you today.",
  ],
  blossom: [
    "That was wonderful! Sam can't wait to see you again.",
    "Great exploring today! Remember, every day is a new adventure.",
    "Goodbye, friend! Sam will keep thinking of fun things for next time.",
  ],
  weaving: [
    "Good session! The ideas we explored today will keep growing, even when you're not thinking about them.",
    "Until next time. Remember — understanding takes time, and that's perfectly okay.",
    "Well done. Sam is always here when you're ready to wonder again.",
  ],
  kindling: [
    "Thank you for your attention today. That's the most generous thing anyone can give.",
    "We've planted some seeds. Let them grow in their own time.",
    "Until next time. Sam doesn't forget the questions we've raised — and neither should you.",
  ],
};

// ---------------------------------------------------------------------------
// Presence-aware lines
// ---------------------------------------------------------------------------

export const MENTOR_DETECTED_LINES = [
  "I notice someone else is here too. Hello! I'll let you guide things.",
  "A mentor is present — wonderful. Sam steps back a little when wise hands are near.",
];

export const MENTOR_LEFT_LINES = [
  "It seems it's just us now. That's perfectly fine — Sam is here.",
  "Just you and Sam again. Shall we continue where we left off?",
];

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

export function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
